import {
  useCurrMapInteractionState,
  useMapInteractionService,
} from "@/models/map-interaction/mapInteractionStateHooks";
import type { PropsWithChildren, ReactNode } from "react";
import { useCallback, useMemo } from "react";

import { DropdownMenuDivider } from "@/components/atoms/DropdownMenuItem/DropdownMenuDivider";
import { DropdownMenuItem } from "@/components/atoms/DropdownMenuItem/DropdownMenuItem";
import { DropdownMenu } from "@/components/molecules/DropdownMenu/DropdownMenu";
import { NestedDropdownMenuItem } from "@/components/molecules/DropdownMenu/NestedDropdownMenuItem/NestedDropdownMenuItem";
import { TooltipWrapper } from "@/components/molecules/TooltipWrapper/TooltipWrapper";
import { FeatureHubGuard } from "@/featureFlags";
import { useProfileAndTabParams } from "@/hooks";
import { compositor } from "@/layers/Compositor";
import type { ContextMenuCtx } from "@/models/map-interaction/mapInteractionStateCtxTypes";
import { MapInteractionEventType } from "@/models/map-interaction/mapInteractionStateEvents";
import { type ToolLocationTarget, useApplyLocationToTool } from "@/overlay/hooks/useApplyLocationToTool";
import { type RootState, useAppDispatch } from "@/reducer";
import { setAllToolLocation } from "@/reducer/TabsState";
import { setAllViewport } from "@/reducer/ViewportsState";
import { PopupPlotType, setPopupPlotState, usePopupPlot } from "@/reducer/plot";
import CrossSectionSvg from "@/style/img/CrossSectionSvg";
import TephigramSvg from "@/style/img/TephigramSvg";
import type { LabeledCoordinates } from "@/types/location";
import { copyCoordinatesToClipboard } from "@/utility/clipboard";
import { FloatingPortal } from "@floating-ui/react";
import { t } from "@lingui/core/macro";
import { type Viewport, ViewportKind } from "@mm/metx-workbench.meteomatics.com";
import { useSelector } from "react-redux";
import { WindowKind, instanceId, useDesktop } from "..";
import { BufferName, useLocalClipboard } from "../LayerStackWindow/ManageActionPanel/LocalClipboardContext";
import { TrackId } from "../window/TrackId";
import { Menu } from "./Menu";
import { ContextMenuActions } from "./constants";

type ToolMenuItem = {
  id: number;
  type: ToolLocationTarget["type"];
  label: string;
  iconName?: string;
  svgIcon?: ReactNode;
};

export function ContextMenuProvider({ children }: PropsWithChildren<unknown>) {
  const { send } = useMapInteractionService();
  const { storeData } = useLocalClipboard<LabeledCoordinates>();
  const dispatch = useAppDispatch();
  const { profileId, tabId } = useProfileAndTabParams();

  const applyLocation = useApplyLocationToTool();
  const { plots, weatherTables, tephigrams } = useSelector((state: RootState) => state.tabGroup.present);

  const mapInteractionState = useCurrMapInteractionState();

  // To save some time and complexity, wire up context menu with react context
  // but in next iterations, it would make more sense to use xstate everywhere
  // TODO Move context to xstate
  const { dispatch: popupPlotDispatch } = usePopupPlot();
  const { dispatch: desktopDispatch, getPosition } = useDesktop();
  const showContextMenu = mapInteractionState.matches("CONTEXT_MENU");

  const setPositionBasedOnTrackId = (trackId: TrackId) => {
    const position = getPosition(instanceId(WindowKind.PopupPlot));
    if (position.isManaged) {
      desktopDispatch({
        type: "setPosition",
        windowId: instanceId(WindowKind.PopupPlot),
        position: { isManaged: true, trackId: trackId },
      });
    }
  };
  const dispatchSetPopupPlotState = (
    coords: ContextMenuCtx["modeOpts"]["coordinates"],
    popupPlotType: PopupPlotType,
    hideLegend = true,
  ) => {
    // TODO: Plot window somehow uses a separate "show" flag to control its visibility status
    // but the desktop system already has the visibility control. Later reconsider this
    // Window can also receive props using "setWindowCustomProps" action, so we might also be able to get rid of the
    // reducer.
    popupPlotDispatch(
      setPopupPlotState({
        type: popupPlotType,
        coords: coords,
        hideLegend: hideLegend,
      }),
    );
    desktopDispatch({
      type: "setWindowVisibility",
      windowId: instanceId(WindowKind.PopupPlot),
      isVisible: true,
    });
  };

  const toolMenuItems = useMemo<ToolMenuItem[]>(() => {
    const items: ToolMenuItem[] = [];

    if (plots) {
      for (const plot of Object.values(plots)) {
        if (!plot) {
          continue;
        }

        items.push({
          id: plot.id,
          type: "plot",
          label: plot.title?.trim() ? plot.title : `Plot #${plot.id}`,
          iconName: "show_chart",
        });
      }
    }

    if (weatherTables) {
      for (const table of Object.values(weatherTables)) {
        if (!table) {
          continue;
        }

        items.push({
          id: table.id,
          type: "weatherTable",
          label: table.title?.trim() ? table.title : `Table #${table.id}`,
          iconName: "table_rows",
        });
      }
    }

    if (tephigrams) {
      for (const tephigram of Object.values(tephigrams)) {
        if (!tephigram) {
          continue;
        }

        items.push({
          id: tephigram.id,
          type: "tephigram",
          label: tephigram.title?.trim() ? tephigram.title : `Tephigram #${tephigram.id}`,
          svgIcon: <TephigramSvg />,
        });
      }
    }

    return items;
  }, [plots, weatherTables, tephigrams]);

  const handleContextMenuAction = (action: ContextMenuActions, context: ContextMenuCtx) => {
    switch (action) {
      case ContextMenuActions.POPUP_PLOT: {
        setPositionBasedOnTrackId(TrackId.Right);
        dispatchSetPopupPlotState(context.modeOpts.coordinates, PopupPlotType.ActiveLayers, false);
        break;
      }
      case ContextMenuActions.POINT_FORECAST_PLOT: {
        setPositionBasedOnTrackId(TrackId.Bottom);
        dispatchSetPopupPlotState(context.modeOpts.coordinates, PopupPlotType.PointForecast);
        break;
      }
      case ContextMenuActions.TEPHIGRAM: {
        setPositionBasedOnTrackId(TrackId.Bottom);
        dispatchSetPopupPlotState(context.modeOpts.coordinates, PopupPlotType.Tephigram);
        break;
      }
      case ContextMenuActions.CROSS_SECTION: {
        setPositionBasedOnTrackId(TrackId.Right);
        dispatchSetPopupPlotState(context.modeOpts.coordinates, PopupPlotType.CrossSection);
        break;
      }
      case ContextMenuActions.COPY_COORDINATES: {
        const location: LabeledCoordinates = { ...context.modeOpts.coordinates };
        storeData(BufferName.Location, location);
        void copyCoordinatesToClipboard({ lat: location.lat, lon: location.lon });
        break;
      }
    }
    send({
      type: MapInteractionEventType.dismiss,
    });
  };

  const handleContextMenuClose = useCallback(() => {
    send({
      type: MapInteractionEventType.dismiss,
    });
  }, [send]);

  const isContextMenuState = mapInteractionState.matches("CONTEXT_MENU");
  const contextMenuCtx = isContextMenuState ? (mapInteractionState.context as ContextMenuCtx) : null;

  const location = useMemo<LabeledCoordinates>(() => {
    if (!contextMenuCtx) {
      return { lat: 0, lon: 0 };
    }
    return { ...contextMenuCtx.modeOpts.coordinates };
  }, [contextMenuCtx]);

  const applyLocationToTool = useCallback(
    (tool: ToolMenuItem, shouldClose = true) => {
      applyLocation({ type: tool.type, id: tool.id }, location);
      if (shouldClose) {
        handleContextMenuClose();
      }
    },
    [applyLocation, handleContextMenuClose, location],
  );

  const applyLocationToAll = useCallback(() => {
    if (!toolMenuItems.length) {
      handleContextMenuClose();
      return;
    }

    dispatch(setAllToolLocation({ location }));

    const newViewport: Viewport = {
      id: 0,
      kind: ViewportKind.ViewportCenterPoint,
      center_lng: location.lon,
      center_lat: location.lat,
      zoom: 10,
      southWest_lng: location.lon,
      southWest_lat: location.lat,
      northEast_lng: location.lon,
      northEast_lat: location.lat,
      id_profile: profileId,
    };
    const tmpScenes = compositor.getScenesByTab(`.${profileId}.${tabId}`);
    const zoomLevel = tmpScenes?.[0]?.getMapViewport().zoomWithPrecision;
    dispatch(setAllViewport({ location: location, zoom: zoomLevel }));
    for (const tmpScene of tmpScenes) {
      if (!tmpScene) {
        return;
      }
      const vp = tmpScene.getMapViewport();
      tmpScene.setViewportDescription({
        ...newViewport,
        zoom: zoomLevel ? zoomLevel : vp.zoom,
      });
    }
  }, [dispatch, handleContextMenuClose, location, toolMenuItems]);

  return (
    <>
      {showContextMenu && contextMenuCtx && (
        <FloatingPortal>
          <Menu context={contextMenuCtx} onClose={handleContextMenuClose} onAction={handleContextMenuAction}>
            <TooltipWrapper
              tooltipText={`${contextMenuCtx.modeOpts.coordinates.lat.toFixed(
                5,
              )}, ${contextMenuCtx.modeOpts.coordinates.lon.toFixed(4)}`}
            >
              <DropdownMenuItem
                label={t`Copy coordinates`}
                onClick={() => handleContextMenuAction(ContextMenuActions.COPY_COORDINATES, contextMenuCtx)}
                iconName={"content_copy"}
                isDestructive={false}
              />

              <NestedDropdownMenuItem
                label={t`Apply location to`}
                iconName={"open_in_new"}
                hoverComponent={
                  <DropdownMenu isOpen={true} onClose={() => {}} withinPortal={false}>
                    <DropdownMenuItem
                      label={t`Apply to all`}
                      onClick={applyLocationToAll}
                      iconName={"layers"}
                      disabled={!toolMenuItems.length}
                    />
                    <DropdownMenuDivider />
                    {toolMenuItems.length ? (
                      toolMenuItems.map((tool) => (
                        <DropdownMenuItem
                          key={`${tool.type}-${tool.id}`}
                          label={tool.label}
                          maxLabelLength={25}
                          iconName={tool.iconName}
                          svgIcon={tool.svgIcon}
                          onClick={() => applyLocationToTool(tool)}
                        />
                      ))
                    ) : (
                      <DropdownMenuItem label={t`No tools available`} disabled />
                    )}
                  </DropdownMenu>
                }
              />
            </TooltipWrapper>
            <DropdownMenuDivider />
            <DropdownMenuItem
              label={t`Plot`}
              onClick={() => handleContextMenuAction(ContextMenuActions.POPUP_PLOT, contextMenuCtx)}
              iconName={"show_chart"}
              isDestructive={false}
            />
            <DropdownMenuItem
              label={t`Meteogram`}
              onClick={() => handleContextMenuAction(ContextMenuActions.POINT_FORECAST_PLOT, contextMenuCtx)}
              iconName={"thermostat"}
              isDestructive={false}
            />
            <DropdownMenuItem
              label={t`Tephigram`}
              onClick={() => handleContextMenuAction(ContextMenuActions.TEPHIGRAM, contextMenuCtx)}
              svgIcon={<TephigramSvg width={16} height={16} />}
              isDestructive={false}
            />
            <FeatureHubGuard flag={"cross_section"}>
              <DropdownMenuItem
                label={t`Cross section`}
                onClick={() => handleContextMenuAction(ContextMenuActions.CROSS_SECTION, contextMenuCtx)}
                svgIcon={<CrossSectionSvg width={16} height={16} />}
                isDestructive={false}
              />
            </FeatureHubGuard>
          </Menu>
        </FloatingPortal>
      )}
      {children}
    </>
  );
}
